import type { CollectionSummaryJSONObject } from '@ikegps/core-api';
import type { CollectionModelBase, CollectionModelField, CollectionModelFieldAnchor } from '@ikegps/core-shared';
import { type ObservableMap, observable, action, makeObservable } from 'mobx';
import type { ICollectionViewController } from '../collection.view/collection.view.ctrl.interface';
import { FieldUploadService } from '../../services/field.upload/field.upload.service';

/**
 * A minimal readonly controller stub that implements the interface needed by
 * CollectionEditViewSubform and CollectionEditViewField for readonly rendering.
 *
 * Key behaviors:
 * - isEditing = false: Uses viewInline() instead of viewEdit()
 * - canInspectCollection() returns false: Disables clickable links on vector fields
 * - All edit operations are no-ops
 */
export class ReadonlyCollectionViewCtrl implements ICollectionViewController {
	readonly kind = 'ReadonlyCollectionViewCtrl';

	constructor() {
		makeObservable(this);
	}

	static is(ctrl: ICollectionViewController): ctrl is ReadonlyCollectionViewCtrl {
		return ctrl.kind === 'ReadonlyCollectionViewCtrl';
	}

	/** Always false - renders fields in readonly mode */
	readonly isEditing = false;

	/** Enable color coding for span colors */
	readonly useColorCoding = true;

	/** Field detail visibility state */
	isFieldDetailShown: ObservableMap<string, boolean> = observable.map<string, boolean>({}, { deep: false });
	/** Open vertex index per line field (accordion). */
	openLineVertex: ObservableMap<string, number> = observable.map<string, number>({}, { deep: false });

	/** No change state in readonly mode */
	readonly changeState: ICollectionViewController['changeState'] = null;

	/** Not uploading in readonly mode */
	readonly isUploading = false;

	/** Inert: readonly mode never renders the edit views that reach into it. */
	readonly upload: FieldUploadService = new FieldUploadService();

	/** Not saving in readonly mode */
	readonly isSaving = false;

	/** Expansion state for subform instances */
	@observable private isFormOpen: ObservableMap<string, boolean> = observable.map<string, boolean>({}, { deep: false });

	/** Default to expanded for preview */
	@observable private isAllExpanded = true;

	/**
	 * Check if a subform instance is expanded.
	 * Always returns true for preview mode (all expanded).
	 */
	isExpanded(instanceId: string): boolean {
		if (this.isAllExpanded) {
			return this.isFormOpen.get(instanceId) !== false;
		}
		return this.isFormOpen.get(instanceId) ?? false;
	}

	/**
	 * Toggle expansion state for a subform instance.
	 */
	@action toggleExpanded(e: MouseEvent, instanceId: string): boolean {
		e.preventDefault();
		let prev = this.isFormOpen.get(instanceId);
		if (prev == null) {
			prev = this.isAllExpanded;
		}
		this.isFormOpen.set(instanceId, !prev);
		return false;
	}

	/**
	 * Toggle field detail visibility.
	 */
	@action toggleFieldDetail(fieldId: string): void {
		if (this.isFieldDetailShown.get(fieldId)) {
			this.isFieldDetailShown.delete(fieldId);
		} else {
			this.isFieldDetailShown.set(fieldId, true);
		}
	}

	/**
	 * Always returns false - disables collection link navigation.
	 * This prevents vector fields from rendering as clickable anchors.
	 */
	canInspectCollection(_summary: CollectionSummaryJSONObject): boolean {
		return false;
	}

	/**
	 * No-op for readonly mode - inspection is disabled.
	 */
	inspectCollection(_revision: string): void {
		// No-op
	}

	/**
	 * No-op for readonly mode.
	 */
	clearField(_field: CollectionModelField): void {
		// No-op
	}

	/**
	 * No-op for readonly mode.
	 */
	clearModalField(_field: CollectionModelField): Promise<void> {
		return Promise.resolve();
	}

	/**
	 * No-op for readonly mode.
	 */
	beginSpanLink(_collection: CollectionModelBase): void {
		// No-op
	}

	/**
	 * Always returns true - field has no clear action in readonly mode.
	 */
	fillAddressDisabled(_field: CollectionModelField): boolean {
		return true;
	}

	/**
	 * Always returns true - field has no clear action in readonly mode.
	 */
	clearErrorDisabled(_field: CollectionModelField): boolean {
		return true;
	}

	/**
	 * No-op for readonly mode.
	 */
	fillAddress(_field: CollectionModelField): Promise<void> {
		return Promise.resolve();
	}

	/**
	 * No-op for readonly mode.
	 */
	clearError(_field: CollectionModelField): void {
		// No-op
	}

	/**
	 * Returns empty string for readonly mode.
	 */
	geocodingButtonTitle(_field: CollectionModelField): string {
		return '';
	}

	/**
	 * Returns null for field joins in readonly mode.
	 */
	getJoin(_field: CollectionModelField): null {
		return null;
	}

	/**
	 * Returns null for field joins in readonly mode.
	 */
	getOppositeJoin(_field: CollectionModelField): null {
		return null;
	}

	/**
	 * No-op for readonly mode.
	 */
	selectAnchorField(_e: MouseEvent, _field: CollectionModelFieldAnchor): void {
		// No-op
	}
}
